import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import Layout from "../components/Layout";
import { FaEnvelope, FaBoxOpen, FaPlay, FaSave, FaTrash, FaPlus, FaCheckCircle, FaExclamationTriangle, FaClock } from "react-icons/fa";

const PendingOrderSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [clientId, setClientId] = useState("");
    
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState("");
    const [availableSkus, setAvailableSkus] = useState([]);
    const [skuSearch, setSkuSearch] = useState("");
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [ratios, setRatios] = useState([]); // [{num: "", den: ""}]
    const [automationHour, setAutomationHour] = useState(17);
    const [automationMinute, setAutomationMinute] = useState(0);
    const [lastSaved, setLastSaved] = useState("");
    const [authorized, setAuthorized] = useState(false);
    const [passInput, setPassInput] = useState("");

    // SECURITY CHECK: DDMMYY
    useEffect(() => {
        const now = new Date();
        const d = String(now.getDate()).padStart(2, '0');
        const m = String(now.getMonth() + 1).padStart(2, '0');
        const y = String(now.getFullYear()).slice(-2);
        const correctPassword = `${d}${m}${y}`;
        if (passInput === correctPassword) {
            setAuthorized(true);
        }
    }, [passInput]);
    
    useEffect(() => {
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                // 1. Try User Profile
                const userSnap = await getDoc(doc(db, "users", user.uid));
                let cid = userSnap.exists() ? userSnap.data().clientId : "";
                
                // 2. Fallback to Company Default
                if (!cid) {
                    const compSnap = await getDoc(doc(db, "companyInfo", "default"));
                    cid = compSnap.exists() ? compSnap.data().clientId : "mdsons";
                }

                setClientId(cid);
                loadSettings(cid);
                loadSkus(cid);
            }
        });
    }, []);

    const loadSettings = async (cid) => {
        console.log("Loading settings for:", cid);
        const snap = await getDoc(doc(db, "settings", `pendingOrderReport_${cid}`));
        if (snap.exists()) {
            const data = snap.data();
            setEmails(data.emails || []);
            setSelectedSkus(data.reportSkus || []);
            
            // Safety check for legacy data (convert strings to arrays)
            const loadedRatios = (data.ratios || []).map(r => ({
                num: Array.isArray(r.num) ? r.num : (r.num ? [r.num] : []),
                den: Array.isArray(r.den) ? r.den : (r.den ? [r.den] : [])
            }));
            setRatios(loadedRatios);
            setAutomationHour(data.automationHour !== undefined ? data.automationHour : 17);
            setAutomationMinute(data.automationMinute !== undefined ? data.automationMinute : 0);
            if (data.updatedAt) {
                setLastSaved(new Date(data.updatedAt).toLocaleString());
            }
        }
        setLoading(false);
    };

    const loadSkus = async (cid) => {
        const q = query(collection(db, "skuMaster"), where("clientId", "==", cid));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a,b) => (a.sku || "").localeCompare(b.sku || ""));
        setAvailableSkus(list);
    };

    const handleAddEmail = () => {
        if (newEmail && !emails.includes(newEmail)) {
            setEmails([...emails, newEmail]);
            setNewEmail("");
        }
    };

    const removeEmail = (email) => {
        setEmails(emails.filter(e => e !== email));
    };

    const toggleSku = (sku) => {
        if (selectedSkus.includes(sku)) {
            setSelectedSkus(selectedSkus.filter(s => s !== sku));
        } else {
            setSelectedSkus([...selectedSkus, sku]);
        }
    };

    const addRatio = () => {
        setRatios([...ratios, { num: [], den: [] }]);
    };

    const toggleRatioSku = (index, field, sku) => {
        const updated = [...ratios];
        const current = updated[index][field] || [];
        if (current.includes(sku)) {
            updated[index][field] = current.filter(s => s !== sku);
        } else {
            updated[index][field] = [...current, sku];
        }
        setRatios(updated);
    };

    const removeRatio = (index) => {
        setRatios(ratios.filter((_, i) => i !== index));
    };

    const saveSettings = async () => {
        if (!clientId) return;
        setSaving(true);
        const timestamp = new Date().toISOString();
        try {
            await setDoc(doc(db, "settings", `pendingOrderReport_${clientId}`), {
                emails,
                reportSkus: selectedSkus,
                ratios,
                automationHour,
                automationMinute,
                updatedAt: timestamp,
                updatedBy: auth.currentUser?.email || "system",
                clientId: clientId
            }, { merge: true });
            setLastSaved(new Date(timestamp).toLocaleString());
            alert("✅ Configuration Hardened in Firebase!");
        } catch (err) {
            console.error(err);
            alert("❌ Error saving to Firebase");
        }
        setSaving(false);
    };

    const runReportNow = async () => {
        if (!emails.length) {
            alert("Please add at least one email address.");
            return;
        }
        setTriggering(true);
        try {
            const functions = getFunctions();
            const manualTrigger = httpsCallable(functions, 'manualTrigger_pendingOrderEmail');
            const result = await manualTrigger({ clientId });
            alert(`Report Triggered! Result: ${result.data.message || "Success"}`);
        } catch (err) {
            console.error(err);
            alert("Error triggering report: " + err.message);
        }
        setTriggering(false);
    };

    if (loading) return <Layout><div style={{padding: 40}}>Loading...</div></Layout>;

    if (!authorized) {
        return (
            <div style={{ position: "fixed", inset: 0, background: "#0f172a", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }}>
                <div style={{ background: "white", padding: "40px", borderRadius: "24px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)", width: "100%", maxWidth: "400px", textAlign: "center" }}>
                    <div style={{ width: "60px", height: "60px", background: "#f1f5f9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                        <FaClock size={30} color="#4f46e5" />
                    </div>
                    <h2 style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginBottom: "10px" }}>Security Check</h2>
                    <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "25px", fontWeight: "500" }}>Enter the Daily Authorization Key to access Automation Settings (DDMMYY).</p>
                    <input 
                        type="password" 
                        placeholder="Enter Key" 
                        value={passInput}
                        onChange={(e) => setPassInput(e.target.value)}
                        maxLength={6}
                        style={{ width: "100%", padding: "15px", borderRadius: "12px", border: "2px solid #e2e8f0", fontSize: "20px", textAlign: "center", letterSpacing: "5px", outline: "none", marginBottom: "20px" }}
                        autoFocus
                    />
                    <div style={{ fontSize: "12px", color: "#94a3b8", fontWeight: "600" }}>Hint: Today's date in 6 digits</div>
                </div>
            </div>
        );
    }

    return (
        <Layout>
            <div style={{ padding: "40px", maxWidth: "1000px", margin: "0 auto" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
                    <div>
                        <h1 style={{ fontSize: "28px", fontWeight: 800, color: "#1e293b", margin: 0 }}>Pending Orders Automation</h1>
                        <p style={{ color: "#64748b", marginTop: "5px" }}>Control your daily executive summary delivery</p>
                    </div>
                    <button 
                        onClick={runReportNow} 
                        disabled={triggering}
                        style={{ padding: "12px 24px", borderRadius: "10px", background: "#f59e0b", color: "#fff", border: "none", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
                    >
                        {triggering ? "Running..." : <><FaPlay /> Run Now</>}
                    </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "30px" }}>
                    {/* EMAIL SECTION */}
                    <div style={{ background: "#fff", padding: "24px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>
                        <h3 style={{ margin: "0 0 20px 0", display: "flex", alignItems: "center", gap: "10px" }}><FaEnvelope color="#4f46e5" /> Recipients</h3>
                        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                            <input 
                                type="email" 
                                value={newEmail} 
                                onChange={e => setNewEmail(e.target.value)}
                                placeholder="Add email (e.g. boss@gmail.com)"
                                style={{ flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e0" }}
                            />
                            <button onClick={handleAddEmail} style={{ padding: "10px 15px", borderRadius: "8px", background: "#4f46e5", color: "#fff", border: "none", cursor: "pointer" }}><FaPlus /></button>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                            {emails.map(email => (
                                <div key={email} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                    <span style={{ fontWeight: 500 }}>{email}</span>
                                    <FaTrash color="#ef4444" style={{ cursor: "pointer" }} onClick={() => removeEmail(email)} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* TIME SECTION */}
                    <div style={{ background: "#fff", padding: "24px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>
                        <h3 style={{ margin: "0 0 20px 0", display: "flex", alignItems: "center", gap: "10px" }}><FaClock color="#f59e0b" /> Automation Time (IST)</h3>
                        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: "5px", fontWeight: 700 }}>HOUR (0-23)</label>
                                <select 
                                    value={automationHour} 
                                    onChange={e => setAutomationHour(parseInt(e.target.value))}
                                    style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #cbd5e0", fontSize: "16px", fontWeight: 600 }}
                                >
                                    {[...Array(24).keys()].map(h => (
                                        <option key={h} value={h}>{String(h).padStart(2, '0')} ({h >= 12 ? (h === 12 ? '12 PM' : h-12 + ' PM') : (h === 0 ? '12 AM' : h + ' AM')})</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: "block", fontSize: "12px", color: "#64748b", marginBottom: "5px", fontWeight: 700 }}>MINUTE (0-59)</label>
                                <select 
                                    value={automationMinute} 
                                    onChange={e => setAutomationMinute(parseInt(e.target.value))}
                                    style={{ width: "100%", padding: "12px", borderRadius: "10px", border: "1px solid #cbd5e0", fontSize: "16px", fontWeight: 600 }}
                                >
                                    {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                                        <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <p style={{ fontSize: "12px", color: "#64748b", marginTop: "15px" }}>The report will be sent daily at <b>{String(automationHour).padStart(2, '0')}:{String(automationMinute).padStart(2, '0')} IST</b>.</p>
                    </div>

                    {/* SKU SECTION */}
                    <div style={{ background: "#fff", padding: "24px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0" }}>
                        <h3 style={{ margin: "0 0 20px 0", display: "flex", alignItems: "center", gap: "10px" }}><FaBoxOpen color="#10b981" /> Report Columns (Select SKUs)</h3>
                        
                        <div style={{ marginBottom: "15px" }}>
                            <input 
                                type="text" 
                                placeholder="Search SKU name or code..."
                                value={skuSearch}
                                onChange={e => setSkuSearch(e.target.value)}
                                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e0", boxSizing: "border-box" }}
                            />
                        </div>

                        <div style={{ maxHeight: "300px", overflowY: "auto", border: "1px solid #f1f5f9", padding: "10px", borderRadius: "8px" }}>
                            {availableSkus.filter(s => 
                                (s.sku || "").toLowerCase().includes(skuSearch.toLowerCase()) || 
                                (s.description || "").toLowerCase().includes(skuSearch.toLowerCase())
                            ).map(s => (
                                <div key={s.id} onClick={() => toggleSku(s.sku)} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "8px", cursor: "pointer", background: selectedSkus.includes(s.sku) ? "#ecfdf5" : "transparent", borderRadius: "6px" }}>
                                    {selectedSkus.includes(s.sku) ? <FaCheckCircle color="#10b981" /> : <div style={{width: 16, height: 16, borderRadius: "50%", border: "2px solid #cbd5e0"}} />}
                                    <span style={{ fontSize: "14px", fontWeight: selectedSkus.includes(s.sku) ? 700 : 500, color: "#1e293b" }}>
                                        {s.sku}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p style={{ fontSize: "12px", color: "#64748b", marginTop: "10px" }}>{selectedSkus.length} SKUs selected for pivot columns.</p>
                    </div>

                    {/* RATIO SECTION */}
                    <div style={{ background: "#fff", padding: "24px", borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e2e8f0", gridColumn: "span 2" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                            <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "10px" }}><FaPlus color="#f43f5e" /> Ratio / Percentage Calculations</h3>
                            <button onClick={addRatio} style={{ padding: "8px 16px", borderRadius: "8px", background: "#f43f5e", color: "#fff", border: "none", cursor: "pointer", fontWeight: 700 }}>+ Add Ratio</button>
                        </div>

                        {ratios.length === 0 ? (
                            <div style={{ padding: "20px", textAlign: "center", background: "#f8fafc", borderRadius: "12px", border: "1px dashed #cbd5e0", color: "#64748b" }}>
                                No ratios defined. These will appear as extra columns in the Excel (e.g., (SKU1+SKU2) / SKU3).
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                {ratios.map((r, i) => (
                                    <div key={i} style={{ background: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", position: "relative" }}>
                                        <button 
                                            onClick={() => removeRatio(i)} 
                                            style={{ position: "absolute", top: "10px", right: "10px", background: "none", border: "none", color: "#ef4444", cursor: "pointer" }}
                                        >
                                            <FaTrash />
                                        </button>

                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "30px" }}>
                                            <div>
                                                <p style={{ margin: "0 0 10px 0", fontSize: "13px", fontWeight: 700, color: "#475569" }}>Numerator (Sum of:)</p>
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                                    {selectedSkus.map(sku => (
                                                        <button 
                                                            key={sku}
                                                            onClick={() => toggleRatioSku(i, "num", sku)}
                                                            style={{ 
                                                                padding: "6px 12px", borderRadius: "20px", fontSize: "11px", cursor: "pointer",
                                                                background: r.num.includes(sku) ? "#4f46e5" : "#fff",
                                                                color: r.num.includes(sku) ? "#fff" : "#475569",
                                                                border: "1px solid " + (r.num.includes(sku) ? "#4f46e5" : "#cbd5e0")
                                                            }}
                                                        >
                                                            {sku}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div>
                                                <p style={{ margin: "0 0 10px 0", fontSize: "13px", fontWeight: 700, color: "#475569" }}>Denominator (Sum of:)</p>
                                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                                    {selectedSkus.map(sku => (
                                                        <button 
                                                            key={sku}
                                                            onClick={() => toggleRatioSku(i, "den", sku)}
                                                            style={{ 
                                                                padding: "6px 12px", borderRadius: "20px", fontSize: "11px", cursor: "pointer",
                                                                background: r.den.includes(sku) ? "#10b981" : "#fff",
                                                                color: r.den.includes(sku) ? "#fff" : "#475569",
                                                                border: "1px solid " + (r.den.includes(sku) ? "#10b981" : "#cbd5e0")
                                                            }}
                                                        >
                                                            {sku}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: "15px", fontSize: "12px", color: "#64748b", fontWeight: 600, background: "#fff", padding: "8px", borderRadius: "6px", display: "inline-block" }}>
                                            Calculation: ({(r.num || []).join(" + ") || "0"}) / ({(r.den || []).join(" + ") || "0"})
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <p style={{ fontSize: "12px", color: "#64748b", marginTop: "15px" }}>Ratios are calculated as: (Sum of Numerator SKUs / Sum of Denominator SKUs) * 100.</p>
                    </div>
                </div>

                <div style={{ marginTop: "30px", background: "#fff", padding: "24px", borderRadius: "16px", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                            <FaExclamationTriangle color="#f59e0b" size={24} />
                            <span style={{ fontSize: "14px", color: "#475569", fontWeight: 500 }}>Settings apply to the daily scheduled automation.</span>
                        </div>
                        {lastSaved && (
                            <span style={{ fontSize: "12px", color: "#10b981", fontWeight: 700, marginLeft: "39px" }}>
                                Last Saved: {lastSaved}
                            </span>
                        )}
                    </div>
                    <button 
                        onClick={saveSettings} 
                        disabled={saving}
                        style={{ padding: "14px 40px", borderRadius: "12px", background: "#1e293b", color: "#fff", border: "none", fontWeight: 800, fontSize: "16px", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}
                    >
                        {saving ? "Saving..." : <><FaSave /> Save Config</>}
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default PendingOrderSettings;
